import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, Sparkles, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/components/theme-provider";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

// Particle animation component
const ParticleBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { theme } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas dimensions
    const setCanvasDimensions = () => {
      if (!canvas) return;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    setCanvasDimensions();
    window.addEventListener("resize", setCanvasDimensions);

    // Particle properties
    const particleCount = 50;
    const particles: Array<{
      x: number;
      y: number;
      radius: number;
      color: string;
      speedX: number;
      speedY: number;
      opacity: number;
    }> = [];

    // Create particles
    for (let i = 0; i < particleCount; i++) {
      const radius = Math.random() * 3 + 1;
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius,
        color: theme === "dark" ? "#ffffff" : "#3b82f6",
        speedX: (Math.random() - 0.5) * 0.5,
        speedY: (Math.random() - 0.5) * 0.5,
        opacity: Math.random() * 0.5 + 0.2,
      });
    }

    // Animation loop
    const animate = () => {
      if (!ctx || !canvas) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw particles
      for (const particle of particles) {
        particle.x += particle.speedX;
        particle.y += particle.speedY;

        // Bounce off edges
        if (particle.x < 0 || particle.x > canvas.width) {
          particle.speedX *= -1;
        }

        if (particle.y < 0 || particle.y > canvas.height) {
          particle.speedY *= -1;
        }

        // Draw particle
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = `${particle.color}${Math.floor(particle.opacity * 255)
          .toString(16)
          .padStart(2, "0")}`;
        ctx.fill();
      }

      // Draw connections between particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 100) {
            ctx.beginPath();
            ctx.strokeStyle = `${particles[i].color}${Math.floor(
              (1 - distance / 100) * 40
            )
              .toString(16)
              .padStart(2, "0")}`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", setCanvasDimensions);
    };
  }, [theme]);

  return <canvas ref={canvasRef} className="absolute inset-0 z-0 opacity-40" />;
};

// Typing animation component
const TypingAnimation = ({
  text,
  className,
}: {
  text: string;
  className?: string;
}) => {
  const [displayText, setDisplayText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayText((prev) => prev + text[currentIndex]);
        setCurrentIndex((prev) => prev + 1);
      }, 100);

      return () => clearTimeout(timeout);
    }
  }, [currentIndex, text]);

  return (
    <span className={cn("relative", className)}>
      {displayText}
      <span className="animate-pulse absolute -right-4 top-0">|</span>
    </span>
  );
};

const Hero = () => {
  const { theme } = useTheme();
  const [showTyping, setShowTyping] = useState(false);
  const benefits = [
    "Expert Tax Advice",
    "Business Registration",
    "Financial Planning",
    "24/7 Support",
  ];

  useEffect(() => {
    // Start typing animation after a delay
    const timeout = setTimeout(() => {
      setShowTyping(true);
    }, 500);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <div className="relative overflow-hidden pt-24 pb-20 sm:pb-28">
      {/* Background with subtle pattern and gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-background to-muted/20 dark:from-gray-950 dark:to-gray-800/60 z-0">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjIiIGZpbGw9IiMyMjIiIGZpbGwtb3BhY2l0eT0iMC4wNSIvPjwvc3ZnPg==')] dark:bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjIiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNCIvPjwvc3ZnPg==')]">
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
        </div>

        {/* Particle animation */}
        <ParticleBackground />

        {/* Subtle animated elements */}
        <div className="absolute top-20 left-1/4 w-32 h-32 bg-primary/10 dark:bg-primary/10 rounded-full mix-blend-normal filter blur-xl opacity-40 animate-float"></div>
        <div className="absolute bottom-20 right-1/4 w-24 h-24 bg-secondary/10 dark:bg-secondary/10 rounded-full mix-blend-normal filter blur-xl opacity-40 animate-float animation-delay-2000"></div>

        {/* Additional animated elements */}
        <div className="absolute top-1/3 right-1/4 w-16 h-16 bg-primary/20 dark:bg-primary/20 rounded-full mix-blend-normal filter blur-lg opacity-30 animate-float animation-delay-4000"></div>
        <div className="absolute bottom-1/3 left-1/3 w-20 h-20 bg-secondary/20 dark:bg-secondary/20 rounded-full mix-blend-normal filter blur-lg opacity-30 animate-float animation-delay-1000"></div>
      </div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          <div className="sm:text-center md:max-w-2xl md:mx-auto lg:col-span-6 lg:text-left">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-5xl tracking-tight font-extrabold text-foreground dark:text-white sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl relative">
                <div className="overflow-hidden">
                  <motion.span
                    className="relative block z-10"
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    Professional
                  </motion.span>
                </div>
                <div className="overflow-hidden">
                  <motion.span
                    className="relative block text-primary z-10"
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                  >
                    {showTyping ? (
                      <TypingAnimation text="Tax & Accounting" />
                    ) : (
                      "Tax & Accounting"
                    )}
                  </motion.span>
                </div>
                <div className="overflow-hidden">
                  <motion.span
                    className="relative block z-10"
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    Services
                  </motion.span>
                </div>
                <motion.div
                  className="absolute -top-5 -left-5 w-16 h-16 bg-primary/10 dark:bg-primary/15 rounded-full blur-lg z-0"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                ></motion.div>
              </h1>
              <motion.p
                className="mt-3 text-base text-muted-foreground dark:text-gray-300 sm:mt-5 sm:text-xl lg:text-lg xl:text-xl relative z-10"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <span className="inline-flex items-center gap-1">
                  <Sparkles className="h-4 w-4 text-primary animate-pulse" />
                  <span>Transform your financial future</span>
                </span>{" "}
                with our expert accounting services. We help businesses and
                individuals navigate the complexities of taxation and finance.
              </motion.p>

              <motion.div
                className="mt-10 sm:max-w-lg sm:mx-auto sm:text-center lg:text-left"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-block"
                >
                  <Button
                    size="lg"
                    className="mr-4 bg-primary hover:bg-primary/90 text-primary-foreground relative overflow-hidden group"
                    onClick={() => (window.location.href = "/signup")}
                  >
                    <span className="relative z-10 flex items-center">
                      Get Started{" "}
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
                    </span>
                    <span className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                  </Button>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-block"
                >
                  <Button
                    variant="outline"
                    size="lg"
                    className="border-border dark:border-gray-700 dark:text-gray-200 dark:hover:text-white relative overflow-hidden group"
                    onClick={() => (window.location.href = "/contact")}
                  >
                    <span className="relative z-10">Contact Us</span>
                    <span className="absolute inset-0 bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                  </Button>
                </motion.div>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="mt-10 space-y-3"
            >
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  className="flex items-center"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + index * 0.1, duration: 0.5 }}
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{
                      delay: 0.7 + index * 0.1,
                      duration: 0.5,
                      type: "spring",
                    }}
                  >
                    <CheckCircle className="h-5 w-5 text-primary mr-2" />
                  </motion.div>
                  <span className="text-muted-foreground dark:text-gray-300">
                    {benefit}
                  </span>
                </motion.div>
              ))}
            </motion.div>
          </div>

          <div className="mt-12 relative sm:max-w-lg sm:mx-auto lg:mt-0 lg:max-w-none lg:mx-0 lg:col-span-6 lg:flex lg:items-center">
            <div className="relative mx-auto w-full rounded-lg shadow-lg lg:max-w-md">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.7 }}
                className="card-wrapper relative block w-full rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                <div className="card-content border border-border dark:border-gray-700">
                  <div className="relative overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80"
                      alt="Professional accounting services"
                      className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
                  </div>
                  <div className="p-8 bg-card dark:bg-gray-800">
                    <div className="text-center">
                      <motion.h3
                        className="text-2xl font-bold mb-4 text-foreground dark:text-white"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8, duration: 0.5 }}
                      >
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-400 dark:from-blue-400 dark:to-blue-200">
                          Trusted by 10,000+
                        </span>
                      </motion.h3>
                      <motion.p
                        className="text-muted-foreground dark:text-gray-300"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.5 }}
                      >
                        Businesses and individuals across Australia choose us
                        for their financial needs
                      </motion.p>

                      <motion.div
                        className="mt-4 flex justify-center space-x-1"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1, duration: 0.5 }}
                      >
                        {[1, 2, 3, 4, 5].map((_, i) => (
                          <motion.div
                            key={i}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{
                              delay: 1 + i * 0.1,
                              duration: 0.5,
                              type: "spring",
                            }}
                          >
                            <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                          </motion.div>
                        ))}
                      </motion.div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
